import type { PropsWithChildren } from 'react';

import { cn } from '@/lib/utils';

/**
 * The shared vocabulary for a page section. Every homepage band, and the MCP connector docs, are
 * built from these — keep the scale and rhythm changes here rather than re-tuning them per section,
 * which is how the site drifted into a dozen slightly different heading sizes.
 */

/** Shared max width and gutters. Also used on its own by pages that aren't a full section band. */
export function ContentWidth({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <div className={cn('mx-auto max-w-[1200px] px-8', className)}>{children}</div>;
}

export function Section({
  id,
  className,
  contentClassName,
  labelledBy,
  children,
}: PropsWithChildren<{
  id?: string;
  className?: string;
  contentClassName?: string;
  labelledBy?: string;
}>) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      // scroll-mt keeps an anchored section clear of the sticky header.
      className={cn(id && 'scroll-mt-20', className)}
    >
      <ContentWidth className={cn('py-28', contentClassName)}>{children}</ContentWidth>
    </section>
  );
}

/** Uppercase mono eyebrow above a section title. */
export function SectionKicker({ children }: { children: string }) {
  return <p className="mb-5 font-mono text-sm tracking-label text-faint-foreground">{children}</p>;
}

export function SectionTitle({
  id,
  className,
  children,
}: PropsWithChildren<{ id?: string; className?: string }>) {
  return (
    <h2 id={id} className={cn('text-4xl leading-display sm:text-5xl', className)}>
      {children}
    </h2>
  );
}

/**
 * A heading whose closing clause carries the accent treatment: brand color, italic, regular weight.
 *
 * The accent is a substring of the full heading rather than a separate half, so the copy stays one
 * value in content.ts and llms.txt / metadata keep reading it as plain text. The trade: editing the
 * heading without editing its accent silently drops the highlight instead of breaking the build.
 */
export function AccentedTitle({ text, accent }: { text: string; accent: string }) {
  const at = text.lastIndexOf(accent);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-normal text-brand italic">{accent}</span>
      {text.slice(at + accent.length)}
    </>
  );
}

/** The lead paragraph under a section title. */
export function SectionLead({ className, children }: PropsWithChildren<{ className?: string }>) {
  return (
    <p className={cn('text-lg leading-relaxed text-muted-foreground', className)}>{children}</p>
  );
}
